/**
 * Temp_zoom — 원문 뷰어(엑셀·이미지) 공용 확대·축소·끌어서 이동 (D-07 §3.5)
 *
 * 표준에 확대·축소 컨트롤이 없어 임시 컴포넌트로 만든다(portfolio 의 확대/축소 막대와 같은 모양). 승격·치환은 UI팀이 판단한다.
 * - 막대(− 비율 ＋ 맞춤)와 Ctrl+휠(터치패드 핀치 포함)이 같은 단계 목록을 쓴다
 * - 배율을 바꿔도 보고 있던 자리(화면 가운데)가 유지되도록 스크롤을 맞춘다
 * - 마우스로 끌어서 이동(스크롤 영역이 넘칠 때만, 손 모양 커서). 터치는 기본 스크롤을 그대로 쓴다
 */
import { useEffect, useLayoutEffect, useRef } from 'react'
import { Button, IconInfoCircle, StatusHint } from '@indipia/ui-core'

export const ZOOM_STEPS = [0.25, 0.4, 0.5, 0.6, 0.75, 1, 1.25, 1.5, 2, 3, 4]
const EPS = 0.001

/** 현재 배율에서 한 단계 위(1)·아래(-1) 배율 — 맞춤 배율처럼 단계 사이에 있어도 가장 가까운 다음 단계로 간다 */
export function stepZoom(cur: number, dir: 1 | -1): number {
  const last = ZOOM_STEPS[ZOOM_STEPS.length - 1]
  const first = ZOOM_STEPS[0]
  return dir > 0
    ? ZOOM_STEPS.find(s => s > cur + EPS) ?? last
    : [...ZOOM_STEPS].reverse().find(s => s < cur - EPS) ?? first
}

const canZoom = (cur: number, dir: 1 | -1): boolean =>
  dir > 0 ? cur < ZOOM_STEPS[ZOOM_STEPS.length - 1] - EPS : cur > ZOOM_STEPS[0] + EPS

/** 막대 — 비율은 안내용이라 aria-live 로 읽히게 한다. "맞춤" 은 이미 맞춤이면 잠근다 */
export function Temp_ZoomBar({ scale, fitted, onStep, onFit }: {
  scale: number
  fitted: boolean
  onStep: (dir: 1 | -1) => void
  onFit: () => void
}) {
  return (
    <div className="flex items-center gap-02" role="group" aria-label="확대·축소">
      <Button size="sm" variant="ghost" className="w-[var(--button-height-sm)] px-0" aria-label="축소" disabled={!canZoom(scale, -1)} onClick={() => onStep(-1)}>−</Button>
      <span className="min-w-08 text-center text-caption tabular-nums text-text-secondary" aria-live="polite">{Math.round(scale * 100)}%</span>
      <Button size="sm" variant="ghost" className="w-[var(--button-height-sm)] px-0" aria-label="확대" disabled={!canZoom(scale, 1)} onClick={() => onStep(1)}>＋</Button>
      <Button size="sm" variant="etc" disabled={fitted} onClick={onFit}>맞춤</Button>
    </div>
  )
}

/** 사용 안내 — 숨은 조작(Ctrl+휠·끌기)을 알려 준다. 막대 아래에 한 줄로 둔다 */
export function Temp_ZoomHint() {
  return (
    <div className="shrink-0 px-04 py-02">
      <StatusHint tone="muted" icon={<IconInfoCircle width={16} height={16} />}>Ctrl+휠로 확대·축소, 끌어서 이동할 수 있어요</StatusHint>
    </div>
  )
}

/** Ctrl+휠 — 휠 한 칸(±100)에 한 단계. 터치패드 핀치는 작은 값이 연달아 오므로 쌓아서 한 단계씩 넘긴다 */
export function useCtrlWheelZoom(el: HTMLElement | null, onStep: (dir: 1 | -1) => void) {
  const latest = useRef(onStep)
  useEffect(() => { latest.current = onStep })
  useEffect(() => {
    if (!el) return
    let acc = 0
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return // 그냥 휠은 스크롤 그대로
      e.preventDefault() // 브라우저 전체 확대가 아니라 원문만 확대한다
      acc += e.deltaMode === 1 ? e.deltaY * 33 : e.deltaY
      if (Math.abs(acc) < 50) return
      latest.current(acc < 0 ? 1 : -1)
      acc = 0
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [el])
}

/**
 * 끌어서 이동 — 마우스 왼쪽 버튼으로 누른 채 움직이면 스크롤 영역이 따라온다(이미지 뷰어처럼).
 * 스크롤이 필요 없으면 아무것도 하지 않고, 스크롤바를 직접 잡는 건 브라우저에 맡긴다. 끄는 동안은 글자 선택을 막는다.
 */
export function useDragPan(el: HTMLElement | null) {
  useEffect(() => {
    if (!el) return
    let drag: { id: number; x: number; y: number; left: number; top: number } | null = null
    const scrollable = () => el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1
    const idleCursor = () => { el.style.cursor = scrollable() ? 'grab' : '' }

    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0 || !scrollable()) return
      const r = el.getBoundingClientRect()
      if (e.clientX - r.left >= el.clientWidth || e.clientY - r.top >= el.clientHeight) return // 스크롤바 위
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop }
      try { el.setPointerCapture(e.pointerId) } catch { /* 이미 끝난 포인터면 캡처 없이 진행 */ }
      el.style.cursor = 'grabbing'
      el.style.userSelect = 'none'
    }
    const onMove = (e: PointerEvent) => {
      if (!drag) { if (e.pointerType === 'mouse') idleCursor(); return }
      if (e.pointerId !== drag.id) return
      el.scrollLeft = drag.left - (e.clientX - drag.x)
      el.scrollTop = drag.top - (e.clientY - drag.y)
    }
    const onEnd = (e: PointerEvent) => {
      if (!drag || e.pointerId !== drag.id) return
      drag = null
      try { el.releasePointerCapture(e.pointerId) } catch { /* 이미 해제됨 */ }
      el.style.userSelect = ''
      idleCursor()
    }
    const noGhost = (e: Event) => e.preventDefault() // 이미지를 끌 때 브라우저의 "그림 끌기" 미리보기를 막는다

    el.addEventListener('pointerdown', onDown)
    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerup', onEnd)
    el.addEventListener('pointercancel', onEnd)
    el.addEventListener('dragstart', noGhost)
    return () => {
      el.removeEventListener('pointerdown', onDown)
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerup', onEnd)
      el.removeEventListener('pointercancel', onEnd)
      el.removeEventListener('dragstart', noGhost)
      el.style.cursor = ''
      el.style.userSelect = ''
    }
  }, [el])
}

/** 배율이 바뀌면 화면 가운데에 있던 자리가 그대로 가운데에 오도록 스크롤을 맞춘다 */
export function useZoomAnchor(el: HTMLElement | null, scale: number) {
  const prev = useRef(scale)
  useLayoutEffect(() => {
    const r = scale / prev.current
    prev.current = scale
    if (!el || r === 1 || !Number.isFinite(r)) return
    el.scrollLeft = (el.scrollLeft + el.clientWidth / 2) * r - el.clientWidth / 2
    el.scrollTop = (el.scrollTop + el.clientHeight / 2) * r - el.clientHeight / 2
  }, [el, scale])
}
